/**
 * QuizStart Component
 *
 * Displays quiz information and manages the logic for starting a quiz session.
 * Handles previous session state, available attempts, and error notifications.
 * Integrates with backend APIs to fetch session data and initialize a new quiz session.
 *
 * @module components/subQuest/quizzes/QuizStart
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Chakra UI components
import {
  Avatar,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';

// External libraries
import axios from 'axios';
import { format, parseJSON } from 'date-fns';
import { MdList } from 'react-icons/md';

// Internal hooks and constants
import useToastNotif from 'hooks/useToastNotif';
import { TOAST_ERROR, TOAST_INFO } from 'utils/constants/toast';

/**
 * QuizStart
 *
 * @param {Object} props - Component props
 * @param {number} props.courseId - Course ID
 * @param {number} props.questId - Quest ID
 * @param {number} props.subQuestId - SubQuest ID
 * @param {boolean} props.hasTimeLimit - Whether the quiz has a time limit
 * @param {boolean} props.unlimitedAttempts - Whether attempts are unlimited
 * @param {number} props.timeLimit - Time limit in minutes
 * @param {number} props.totalMark - Total score for the quiz
 * @param {number} props.passingScore - Passing score
 * @param {string} props.scoreType - Score type (e.g., 'Actual Score')
 * @param {Function} props.handleStartQuiz - Callback to start the quiz
 * @param {Function} props.handleOpenQuestionnaireMd - Callback to open questionnaire modal
 * @returns {JSX.Element}
 */
function QuizStart({
  courseId,
  questId,
  subQuestId,
  hasTimeLimit,
  unlimitedAttempts,
  timeLimit,
  totalMark,
  passingScore,
  scoreType,
  handleStartQuiz,
  handleOpenQuestionnaireMd,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [prevSession, setPrevSession] = useState(null);

  const borderColor = useColorModeValue('gray.200', 'teal');
  const buttonColor = useColorModeValue('red', 'teal');

  const { generateToastNotif } = useToastNotif();

  const prevRawScore = prevSession?.score ?? 0;
  const prevPercentScore =
    totalMark > 0
      ? `${parseFloat((prevRawScore / totalMark) * 100).toFixed(2)}%`
      : `${parseFloat(prevRawScore.toFixed(2))}`;

  const prevScoreDisplay =
    scoreType !== 'Actual Score'
      ? prevPercentScore
      : parseFloat(prevRawScore.toFixed(2));

  // Fetch previous quiz session info on mount
  useEffect(() => {
    const fetchLastSession = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `/quizzes/${courseId}/${questId}/${subQuestId}`
        );

        const { data } = response;
        const {
          dateFinished,
          dateStarted,
          result,
          score,
          showResult,
          showAnswers,
          attemptsLeft,
          canStart,
          timeSecondsLeft,
          dateBeforeNextAttempt,
          questionnaires,
        } = data;

        setPrevSession({
          dateFinished,
          dateStarted,
          result,
          score,
          showResult,
          showAnswers,
          canStart,
          attemptsLeft,
          timeSecondsLeft,
          dateBeforeNextAttempt,
          questionnaires,
        });
      } catch {
        setPrevSession(null);
      }
      setIsLoading(false);
    };

    fetchLastSession();
  }, [courseId, questId, subQuestId]);

  // Auto-start quiz if previous session is in progress
  useEffect(() => {
    if (
      prevSession?.canStart &&
      prevSession?.dateFinished == null &&
      prevSession?.dateStarted
    ) {
      handleStartQuiz(prevSession.timeSecondsLeft);
    }
    // Only run when prevSession changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevSession]);

  // Initialize a new quiz session
  const handleInitializeQuiz = async () => {
    if (prevSession?.canStart) {
      if (prevSession.dateStarted && !prevSession.dateFinished) {
        handleStartQuiz(prevSession.timeSecondsLeft);
        return;
      }

      setIsLoading(true);

      try {
        await axios.post('/quizzes/start', {
          courseId,
          questId,
          subQuestId,
        });

        generateToastNotif(
          TOAST_INFO,
          'Quiz session started',
          'Good luck and enjoy the learning experience!',
          true,
          false
        );

        handleStartQuiz(prevSession.timeSecondsLeft);
      } catch (error) {
        const errorMessage = 'There something wrong with the request.';
        const errorObject = error.response?.data?.errors;

        if (errorObject) {
          Object.keys(errorObject).forEach((key) => {
            if (Array.isArray(errorObject[key])) {
              generateToastNotif(
                TOAST_ERROR,
                'Error',
                errorObject[key][0],
                true,
                false
              );
            }
          });
        } else {
          generateToastNotif(TOAST_ERROR, 'Error', errorMessage, true, false);
        }
      }

      setIsLoading(false);
    }
  };

  return (
    <Flex h="100%">
      <Center w="100%">
        <Box w={250} borderRadius="md" border="1px" borderColor={borderColor}>
          <Center>
            <Text px={4} pt={4} fontSize="md" fontWeight={500}>
              Quiz Information
            </Text>
          </Center>

          <Divider my={3} />

          <Box px={4} pb={4} fontSize="sm">
            {/* Quiz time limit info */}
            <Text>
              Time Limit:&nbsp;
              <Text as="span" fontWeight={500}>
                {hasTimeLimit ? timeLimit : 'No time limit'}&nbsp;
                {hasTimeLimit && timeLimit > 0 && 'minute(s)'}
              </Text>
            </Text>
            {/* Total score */}
            <Text>
              Total Score:&nbsp;
              <Text as="span" fontWeight={500}>
                {totalMark}
              </Text>
            </Text>
            {/* Passing score */}
            <Text>
              Passing Score:&nbsp;
              <Text as="span" fontWeight={500}>
                {passingScore}
                {scoreType !== 'Actual Score' ? '%' : ''}
              </Text>
            </Text>
            {/* Available attempts */}
            {prevSession && (
              <Text>
                Available Attempts:&nbsp;
                <Text as="span" fontWeight={500}>
                  {!unlimitedAttempts ? prevSession.attemptsLeft : 'Unlimited'}
                  &nbsp;
                  {!unlimitedAttempts &&
                    prevSession.attemptsLeft > 0 &&
                    'attempt(s)'}
                </Text>
              </Text>
            )}

            {/* Start button */}
            <Button
              mt={4}
              w="100%"
              colorScheme={buttonColor}
              onClick={handleInitializeQuiz}
              isDisabled={!prevSession?.canStart}
              isLoading={isLoading}
            >
              Start
            </Button>

            {/* Max attempts reached */}
            {!isLoading &&
              !prevSession?.canStart &&
              !unlimitedAttempts &&
              prevSession?.attemptsLeft === 0 && (
                <Flex justify="center" my={2}>
                  <Text color="red" fontSize="xs" fontWeight={500}>
                    ● Maximum quiz attempts reached.
                  </Text>
                </Flex>
              )}

            {/* Wait before next attempt */}
            {!isLoading &&
              !prevSession?.canStart &&
              prevSession?.dateBeforeNextAttempt && (
                <Flex justify="center" my={2}>
                  <Text color="red" fontSize="xs" fontWeight={500}>
                    ● You need to wait until&nbsp;
                    {format(
                      parseJSON(prevSession.dateBeforeNextAttempt),
                      'MMMM d, yyyy (h:mm a)'
                    )}{' '}
                    &nbsp;before you can retake the quiz, provided you have
                    available attempts
                  </Text>
                </Flex>
              )}

            {/* Previous attempt result */}
            {prevSession?.showResult && prevSession?.dateFinished && (
              <Box my={2}>
                <Divider mb={2} />

                <Text fontSize="xs" fontWeight={500} mb={2}>
                  Previous Attempt
                </Text>

                <Text fontSize="xs">
                  {format(
                    parseJSON(prevSession.dateFinished),
                    'MMMM d, yyyy (h:mm a)'
                  )}
                </Text>
                <Text fontSize="xs" mb={1}>
                  Score:&nbsp;
                  <Text as="span" fontWeight={500}>
                    {prevScoreDisplay}
                    &nbsp;
                    <Avatar
                      size="2xs"
                      bg={prevSession.result === 'Pass' ? 'green' : 'red'}
                      icon={
                        prevSession.result === 'Pass' ? (
                          <CheckIcon boxSize="2" />
                        ) : (
                          <CloseIcon boxSize="2" />
                        )
                      }
                      mr={2}
                    />
                  </Text>
                </Text>
                {prevSession.showAnswers && (
                  <Button
                    colorScheme={buttonColor}
                    rightIcon={<MdList />}
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      handleOpenQuestionnaireMd(prevSession.questionnaires);
                    }}
                  >
                    Display Result
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Center>
    </Flex>
  );
}

QuizStart.propTypes = {
  courseId: PropTypes.number.isRequired,
  questId: PropTypes.number.isRequired,
  subQuestId: PropTypes.number.isRequired,
  hasTimeLimit: PropTypes.bool.isRequired,
  unlimitedAttempts: PropTypes.bool.isRequired,
  timeLimit: PropTypes.number.isRequired,
  totalMark: PropTypes.number.isRequired,
  passingScore: PropTypes.number.isRequired,
  scoreType: PropTypes.string.isRequired,
  handleStartQuiz: PropTypes.func.isRequired,
  handleOpenQuestionnaireMd: PropTypes.func.isRequired,
};

export default QuizStart;
